import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  output,
} from '@angular/core';

import { StadiaLatLng, StadiaMarkerIcon } from '../stadia-maps.types';
import { StadiaMapsContextService } from '../stadia-maps-context.service';
import type * as L from 'leaflet';
import { StadiaMapsMarkerService } from './stadia-maps-marker.service';
import { StadiaMapsLoaderService } from '../stadia-maps-loader.service';

const DEFAULT_MARKER_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path d="M12 0C5.372 0 0 5.372 0 12c0 9 12 24 12 24s12-15 12-24C24 5.372 18.628 0 12 0z" fill="#3B3C66"/><circle cx="12" cy="12" r="5" fill="white"/></svg>`;

@Component({
  selector: 'pxw-stadia-maps-marker',
  imports: [],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StadiaMapsMarkerService],
})
export class StadiaMapsMarkerComponent implements OnInit, OnDestroy {
  private stadiaMapsContext = inject(StadiaMapsContextService);
  private stadiaMapsMarkerService = inject(StadiaMapsMarkerService);
  private stadiaMapsLoaderService = inject(StadiaMapsLoaderService);

  readonly lat = input.required<number>();
  readonly lng = input.required<number>();
  readonly draggable = input<boolean>(false);
  readonly icon = input<StadiaMarkerIcon | null>(null);

  readonly markerDragEnd = output<StadiaLatLng>();
  readonly markerClick = output<StadiaLatLng>();

  protected markerInstance: L.Marker | null = null;
  protected isDestroyed = false;

  constructor() {
    effect(() => {
      const lat = this.lat();
      const lng = this.lng();

      if (this.markerInstance && typeof lat === 'number' && typeof lng === 'number') {
        this.markerInstance.setLatLng([lat, lng]);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    const L = await this.stadiaMapsLoaderService.getLeaflet();

    if (this.isDestroyed) {
      return;
    }

    const icon = this.icon();

    const markerIcon = icon
      ? L.icon({
          iconUrl: icon.url,
          iconSize: [icon.width, icon.height],
          iconAnchor: [icon.width / 2, icon.height],
          popupAnchor: [0, -icon.height],
        })
      : L.icon({
          iconUrl: 'data:image/svg+xml;base64,' + btoa(DEFAULT_MARKER_ICON_SVG),
          iconSize: [24, 36],
          iconAnchor: [12, 36],
          popupAnchor: [0, -36],
        });

    this.markerInstance = L.marker([this.lat(), this.lng()], {
      draggable: this.draggable(),
      icon: markerIcon,
    });

    this.stadiaMapsMarkerService.registerMarker(this.markerInstance);

    this.markerInstance.on('click', () => {
      if (!this.markerInstance) {
        return;
      }
      const pos = this.markerInstance.getLatLng();
      this.markerClick.emit({ lat: pos.lat, lng: pos.lng });
    });

    if (this.draggable()) {
      this.markerInstance.on('dragend', () => {
        if (!this.markerInstance) {
          return;
        }
        const pos = this.markerInstance.getLatLng();
        this.markerDragEnd.emit({ lat: pos.lat, lng: pos.lng });
      });
    }

    this.markerInstance.addTo(this.stadiaMapsContext.mapInstance);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.markerInstance) {
      this.markerInstance.remove();

      this.markerInstance = null;
    }
  }
}
